import { create } from 'zustand';
import { apiFetchAuthed } from '../apiHelpers';
import useUserStore from './useUserStore';

// Emotions that don't auto-revert to neutral
const STICKY_EMOTIONS = ['neutral', 'thinking'];
let notesOpening = false; // one openNotes at a time
let emotionTimeoutId = null;

const useChatStore = create((set, get) => ({
  // UI state
  isOpen: false,
  isSidebarOpen: false,
  sidebarTab: 'conversations', // 'conversations' | 'notes'
  // Text the input bar takes once, then clears (a reply started from a note).
  draft: '',
  // Desktop docked panel width (persists across open/close within session)
  panelWidth: 420,
  // FlerpieChatBot is mounted on desktop on this page, so the nav shows his launcher.
  dockReady: false,
  // Conversation data
  conversations: [],
  activeConversationId: null,
  messages: [],

  // Streaming state
  isStreaming: false,
  streamBuffer: '',
  progressLabel: null,
  pendingNotes: 0,
  // Every note sent to me, newest first: { id, body, from_name, created_at, delivered_at }
  notes: [],
  notesLoading: false,
  // The open conversation's thread shape: { kind, shared, mine, members: [{id,label,owner}] }
  // kind is 'chat' or 'notes'; each person has one notes thread, where notes land.
  activeThread: null,
  abortController: null,

  // Avatar emotion
  currentEmotion: 'neutral',

  // Rate limit cooldown (timestamp when cooldown expires, 0 = no cooldown)
  cooldownUntil: 0,

  // User profile (role onboarding)
  userProfile: null,

  // --- UI Actions ---

  toggleOpen: () => {
    const { isOpen } = get();
    const opening = !isOpen;
    set({ isOpen: opening });
    if (opening) {
      get().setEmotion('greeting');
      // Opening him while he waves means you came for the note: the sidebar opens on Notes.
      if (get().pendingNotes > 0) set({ isSidebarOpen: true, sidebarTab: 'notes' });
      if (!get().userProfile) get().fetchProfile();
      get().fetchConversations().then(() => {
        if (!get().isOpen) return; // Guard: user may have closed during fetch
        const { conversations, activeConversationId } = get();
        if (conversations.length === 0 && !activeConversationId) get().createConversation();
        else if (!activeConversationId) {
          const newestChat = conversations.find((c) => c.kind !== 'notes');
          if (newestChat) get().selectConversation(newestChat.id);
          else get().createConversation(); // only the notes thread exists
        }
      });
    }
  },

  toggleSidebar: () => set((s) => ({ isSidebarOpen: !s.isSidebarOpen })),

  setSidebarTab: (sidebarTab) => set({ sidebarTab }),

  setDraft: (draft) => set({ draft }),

  setPanelWidth: (panelWidth) => set({ panelWidth }),

  setDockReady: (dockReady) => set({ dockReady }),

  fetchProfile: async () => {
    try {
      const data = await apiFetchAuthed('/ai/profile');
      if (data) set({ userProfile: data });
    } catch (err) {
      console.error('Failed to fetch AI profile:', err);
    }
  },

  setRole: async (role) => {
    try {
      const data = await apiFetchAuthed('/ai/profile', {
        method: 'PATCH',
        body: JSON.stringify({ role_at_firefly: role }),
      });
      if (data) set({ userProfile: data });
    } catch (err) {
      console.error('Failed to set role:', err);
    }
  },

  // What Flerpie remembers about me. Both calls answer with the whole profile.
  addFact: async (fact) => {
    try {
      const data = await apiFetchAuthed('/ai/profile/facts', {
        method: 'POST',
        body: JSON.stringify({ fact }),
      });
      if (data) set({ userProfile: data });
      return true;
    } catch (err) {
      console.error('Failed to add fact:', err);
      return false;
    }
  },

  // Also marks any answer on screen that stored this fact, so its Undo cannot
  // fire a second delete after the panel already forgot it.
  forgetFact: async (factId, messageId) => {
    try {
      const data = await apiFetchAuthed(`/ai/profile/facts/${factId}`, { method: 'DELETE' });
      if (data) set({ userProfile: data });
      set((s) => ({
        messages: s.messages.map((m) => ((m.id === messageId || (m.remember && m.remember.id === factId)) && m.remember
          ? { ...m, remember: { ...m.remember, forgotten: true } }
          : m)),
      }));
      return true;
    } catch (err) {
      console.error('Failed to forget fact:', err);
      return false;
    }
  },

  setEmotion: (emotion) => {
    clearTimeout(emotionTimeoutId);
    set({ currentEmotion: emotion });

    if (!STICKY_EMOTIONS.includes(emotion)) {
      emotionTimeoutId = setTimeout(() => {
        if (get().currentEmotion === emotion) {
          set({ currentEmotion: emotion === 'laughing' ? 'steady' : 'neutral' });
        }
      }, 5000);
    }
  },

  // --- Conversation CRUD ---

  fetchConversations: async () => {
    try {
      const data = await apiFetchAuthed(`/ai/conversations`);
      if (data) set({ conversations: data });
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  },

  selectConversation: async (id) => {
    clearTimeout(emotionTimeoutId);
    set({ activeConversationId: id, messages: [], activeThread: null, currentEmotion: 'neutral' });
    try {
      const data = await apiFetchAuthed(`/ai/conversations/${id}`);
      if (data && get().activeConversationId === id) {
        get().applyConversation(data);
        const lastAssistant = [...(data.messages || [])].reverse().find(m => m.role === 'assistant');
        if (lastAssistant?.emotion) {
          set({ currentEmotion: lastAssistant.emotion });
        }
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  },

  // A conversation as the server returns it: messages plus the thread shape.
  applyConversation: (data) => {
    if (!data || data.id !== get().activeConversationId) return;
    set((s) => ({
      messages: data.messages || [],
      activeThread: { kind: data.kind || 'chat', shared: !!data.shared, mine: data.mine !== false, members: data.members || [] },
      conversations: s.conversations.map((c) => (c.id === data.id
        ? { ...c, shared: !!data.shared, mine: data.mine !== false, with: (data.members || []).filter((m) => m.id !== useUserStore.getState().user.id).map((m) => m.label.split(' ')[0]) }
        : c)),
    }));
  },

  // A shared thread moves while you watch it: pull what the others said.
  // Messages already on screen keep their place; only new ids are appended.
  refreshActiveThread: async () => {
    const id = get().activeConversationId;
    if (!id || !get().activeThread || !get().activeThread.shared || get().isStreaming) return;
    try {
      const data = await apiFetchAuthed(`/ai/conversations/${id}`);
      if (!data || get().activeConversationId !== id || get().isStreaming) return;
      const mine = get().messages;
      const known = new Set(mine.map((m) => m.id));
      // The copy of my own question I showed before the server stored it has a
      // local id; the server's row for it is the same words from me.
      const optimistic = mine.filter((m) => String(m.id).startsWith('user-'));
      const fresh = (data.messages || []).filter((m) => !known.has(m.id)
        && !(m.role === 'user' && optimistic.some((o) => o.content === m.content)));
      if (fresh.length) {
        set((s) => ({ messages: [...s.messages, ...fresh].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)) }));
      }
      set({ activeThread: { kind: data.kind || get().activeThread?.kind || 'chat', shared: !!data.shared, mine: data.mine !== false, members: data.members || [] } });
    } catch (err) {
      console.error('Failed to refresh thread:', err);
    }
  },

  // Step out of a thread someone else owns.
  leaveConversation: async (id) => {
    try {
      await apiFetchAuthed(`/ai/conversations/${id}/members/me`, { method: 'DELETE', skipJsonParse: true });
      set((s) => {
        const conversations = s.conversations.filter((c) => c.id !== id);
        const wasActive = s.activeConversationId === id;
        return {
          conversations,
          activeConversationId: wasActive ? (conversations.find((c) => c.kind !== 'notes')?.id || null) : s.activeConversationId,
          messages: wasActive ? [] : s.messages,
          activeThread: wasActive ? null : s.activeThread,
        };
      });
      if (get().activeConversationId && !get().activeThread) get().selectConversation(get().activeConversationId);
    } catch (err) {
      console.error('Failed to leave conversation:', err);
    }
  },

  createConversation: async () => {
    try {
      const data = await apiFetchAuthed(`/ai/conversations`, {
        method: 'POST',
      });
      if (data) {
        clearTimeout(emotionTimeoutId);
        set((s) => ({
          conversations: [data, ...s.conversations],
          activeConversationId: data.id,
          messages: [],
          activeThread: { kind: 'chat', shared: false, mine: true, members: [] },
          currentEmotion: 'neutral',
        }));
      }
    } catch (err) {
      console.error('Failed to create conversation:', err);
    }
  },

  deleteConversation: async (id) => {
    try {
      await apiFetchAuthed(`/ai/conversations/${id}`, {
        method: 'DELETE',
        skipJsonParse: true,
      });
      set((s) => {
        const conversations = s.conversations.filter(c => c.id !== id);
        const wasActive = s.activeConversationId === id;
        return {
          conversations,
          activeConversationId: wasActive ? (conversations.find((c) => c.kind !== 'notes')?.id || null) : s.activeConversationId,
          messages: wasActive ? [] : s.messages,
          activeThread: wasActive ? null : s.activeThread,
        };
      });
      if (get().activeConversationId && !get().activeThread) get().selectConversation(get().activeConversationId);
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  },

  // --- Streaming ---

  appendStreamChunk: (text) => set((s) => ({ streamBuffer: s.streamBuffer + text })),

  // What Flerpie is checking right now, in his words, while the bubble is empty.
  setProgressLabel: (label) => set({ progressLabel: label || null }),

  // A note's Sent state stays on its answer for the rest of the session.
  setMessageNote: (messageId, note) => set((s) => ({
    messages: s.messages.map((m) => (m.id === messageId ? { ...m, note } : m)),
  })),

  // --- Notes waiting for me ---

  // How many notes are waiting. While the panel is closed, a waiting note
  // keeps Flerpie waving until it is read.
  fetchPendingNotes: async () => {
    try {
      const data = await apiFetchAuthed('/ai/notes/pending');
      const count = (data && data.count) || 0;
      const hadSome = get().pendingNotes > 0;
      set({ pendingNotes: count });
      if (count > 0 && !get().isOpen) {
        clearTimeout(emotionTimeoutId);
        set({ currentEmotion: 'greeting' });
      } else if (count === 0 && hadSome && !get().isOpen && get().currentEmotion === 'greeting') {
        set({ currentEmotion: 'neutral' }); // read somewhere else; stop waving
      }
      return count;
    } catch (err) {
      console.error('Failed to check notes:', err);
      return 0;
    }
  },

  // Open my notes thread. The server makes it the first time it is asked for.
  openNotes: async () => {
    // Never mid-answer: finalizeStream would land the answer in the wrong view.
    if (get().isStreaming || notesOpening) return;
    if (get().activeThread && get().activeThread.kind === 'notes') { get().deliverNotes(); return; }
    const from = get().activeConversationId;
    notesOpening = true;
    try {
      const data = await apiFetchAuthed('/ai/notes/thread');
      // Guard: the user may have picked another conversation during the fetch.
      if (!data || get().activeConversationId !== from || get().isStreaming) return;
      clearTimeout(emotionTimeoutId);
      set({ activeConversationId: data.id, messages: [], activeThread: null, currentEmotion: 'neutral' });
      if (!get().conversations.some((c) => c.id === data.id)) {
        set((s) => ({
          conversations: [{ id: data.id, title: data.title, kind: 'notes', shared: false, mine: true, with: [], created_at: data.created_at, updated_at: data.updated_at }, ...s.conversations],
        }));
      }
      get().applyConversation(data);
      const lastAssistant = [...(data.messages || [])].reverse().find(m => m.role === 'assistant');
      if (lastAssistant?.emotion) {
        set({ currentEmotion: lastAssistant.emotion });
      }
      if (get().pendingNotes > 0) get().deliverNotes();
    } catch (err) {
      console.error('Failed to open notes:', err);
    } finally {
      notesOpening = false;
    }
  },

  // Hand the waiting notes over into the notes thread, only while it is open.
  deliverNotes: async () => {
    const thread = get().activeThread;
    if (!thread || thread.kind !== 'notes' || get().pendingNotes === 0 || get().isStreaming) return;
    try {
      const data = await apiFetchAuthed('/ai/notes/deliver', { method: 'POST' });
      const delivered = (data && data.messages) || [];
      if (delivered.length && get().activeConversationId === data.conversation_id) {
        clearTimeout(emotionTimeoutId);
        set((s) => ({ messages: [...s.messages, ...delivered.map((m) => ({ ...m, _isNew: true }))], currentEmotion: 'greeting' }));
      }
      set({ pendingNotes: 0 });
    } catch (err) {
      console.error('Failed to deliver notes:', err);
    }
  },

  // The Notes tab's list. Answers whether it loaded, for the tab's error line.
  fetchNotes: async () => {
    set({ notesLoading: true });
    try {
      const data = await apiFetchAuthed('/ai/notes');
      set({ notes: (data && data.notes) || [], notesLoading: false });
      return true;
    } catch (err) {
      console.error('Failed to fetch notes:', err);
      set({ notesLoading: false });
      return false;
    }
  },

  // Hand the waiting notes into the notes thread from anywhere. They only
  // appear on screen if that thread is the one open.
  deliverNotesQuietly: async () => {
    if (get().pendingNotes === 0) return;
    try {
      const data = await apiFetchAuthed('/ai/notes/deliver', { method: 'POST' });
      const delivered = (data && data.messages) || [];
      // Never into a running answer: finalizeStream would land after the notes.
      if (delivered.length && get().activeConversationId === data.conversation_id && !get().isStreaming) {
        clearTimeout(emotionTimeoutId);
        set((s) => ({ messages: [...s.messages, ...delivered.map((m) => ({ ...m, _isNew: true }))], currentEmotion: 'greeting' }));
      }
      set({ pendingNotes: 0 });
    } catch (err) {
      console.error('Failed to deliver notes:', err);
    }
  },

  // Replies happen in the notes thread, started with who the reply is for.
  replyToNote: async (note) => {
    await get().openNotes();
    // openNotes refuses mid-answer or while another open is in flight.
    if (!get().activeThread || get().activeThread.kind !== 'notes') return;
    set({ draft: `Tell ${note.from_name}: `, isSidebarOpen: false });
  },

  // A verdict given on an answer stays on it for the rest of the session.
  setMessageFeedback: (messageId, feedback) => set((s) => ({
    messages: s.messages.map((m) => (m.id === messageId ? { ...m, feedback } : m)),
  })),

  finalizeStream: (message) => {
    // Set emotion immediately (no auto-revert yet — typing effect will trigger that)
    clearTimeout(emotionTimeoutId);
    set((s) => ({
      isStreaming: false,
      streamBuffer: '',
      progressLabel: null,
      abortController: null,
      messages: [...s.messages.filter(m => m.id !== '__streaming'), { ...message, _isNew: true }],
      currentEmotion: message.emotion || 'neutral',
    }));
  },

  // Called by the typing effect when it finishes revealing text
  onTypingComplete: () => {
    // Clear _isNew flag so re-mounts don't re-animate
    set((s) => ({
      messages: s.messages.map(m => m._isNew ? { ...m, _isNew: false } : m),
    }));

    const emotion = get().currentEmotion;
    if (!STICKY_EMOTIONS.includes(emotion)) {
      clearTimeout(emotionTimeoutId);
      emotionTimeoutId = setTimeout(() => {
        if (get().currentEmotion === emotion) {
          if (emotion === 'laughing') {
            set({ currentEmotion: 'steady' });
            emotionTimeoutId = setTimeout(() => {
              if (get().currentEmotion === 'steady') {
                set({ currentEmotion: 'neutral' });
              }
            }, 5000);
          } else {
            set({ currentEmotion: 'neutral' });
          }
        }
      }, 5000);
    }
  },

  startCooldown: (seconds = 60) => set({ cooldownUntil: Date.now() + seconds * 1000 }),
  clearCooldown: () => set({ cooldownUntil: 0 }),

  cancelStream: () => {
    const { abortController } = get();
    if (abortController) abortController.abort();
    set((s) => ({
      isStreaming: false,
      streamBuffer: '',
      abortController: null,
      messages: s.messages.filter(m => m.id !== '__streaming'),
    }));
  },

  // Send a message — triggers SSE stream (wired in useSSEStream hook)
  addUserMessage: (content) => set((s) => ({
    messages: [...s.messages, {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    }],
  })),

  startStreaming: (abortController) => {
    clearTimeout(emotionTimeoutId);
    set((s) => ({
    isStreaming: true,
    streamBuffer: '',
    progressLabel: null,
    abortController,
    currentEmotion: 'thinking',
    messages: [...s.messages, {
      id: '__streaming',
      role: 'assistant',
      content: '',
      emotion: 'thinking',
      created_at: new Date().toISOString(),
    }],
  }));
  },

  // Update conversation title in the list after first message
  updateConversationTitle: (id, title) => set((s) => ({
    conversations: s.conversations.map(c =>
      c.id === id ? { ...c, title } : c
    ),
  })),

  // --- Cleanup ---

  reset: () => {
    clearTimeout(emotionTimeoutId);
    emotionTimeoutId = null;
    set({
    isOpen: false,
    pendingNotes: 0,
    notes: [],
    notesLoading: false,
    isSidebarOpen: false,
    sidebarTab: 'conversations',
    draft: '',
    panelWidth: 420,
    conversations: [],
    activeConversationId: null,
    messages: [],
    isStreaming: false,
    streamBuffer: '',
    abortController: null,
    currentEmotion: 'neutral',
    cooldownUntil: 0,
    userProfile: null,
  });
  },
}));

export default useChatStore;
